import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface EditAssetDialogProps {
  asset: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAssetUpdated: () => void;
  customLocations: string[];
  loaningEnabled: boolean;
}

export default function EditAssetDialog({ 
  asset, 
  open, 
  onOpenChange, 
  onAssetUpdated,
  customLocations,
  loaningEnabled
}: EditAssetDialogProps) {
  const { toast } = useToast();
  const [showCustomLocation, setShowCustomLocation] = useState(false);
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    custom_category: "",
    location: "",
    custom_location: "",
    value: "",
    description: "",
    loanable: false
  });

  useEffect(() => {
    if (asset) {
      setFormData({
        name: asset.name || "",
        category: asset.custom_category ? "other" : asset.category || "",
        custom_category: asset.custom_category || "",
        location: customLocations.includes(asset.location) || ["garage", "store_room", "shipping_container"].includes(asset.location) 
          ? asset.location 
          : "other",
        custom_location: !["garage", "store_room", "shipping_container"].includes(asset.location) && !customLocations.includes(asset.location)
          ? asset.location
          : "",
        value: asset.value?.toString() || "",
        description: asset.description || "",
        loanable: asset.loanable || false
      });
      setShowCustomCategory(!!asset.custom_category);
      setShowCustomLocation(!["garage", "store_room", "shipping_container"].includes(asset.location) && !customLocations.includes(asset.location));
    }
  }, [asset, customLocations]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const location = formData.location === "other" ? formData.custom_location : formData.location;
    const category = formData.category === "other" ? formData.custom_category : formData.category;

    const { error } = await supabase
      .from('assets')
      .update({
        name: formData.name,
        category: category,
        custom_category: formData.category === "other" ? formData.custom_category : null,
        location: location,
        value: formData.value ? parseFloat(formData.value) : null,
        description: formData.description,
        loanable: formData.loanable
      })
      .eq('id', asset.id);

    if (error) {
      toast({
        title: "Error updating asset",
        description: error.message,
        variant: "destructive"
      });
    } else {
      toast({
        title: "Asset updated",
        description: "The asset has been updated successfully.",
      });
      onAssetUpdated();
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Asset</DialogTitle>
          <DialogDescription>Update asset information</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-asset-name">Asset Name</Label>
            <Input
              id="edit-asset-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-asset-category">Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => {
                  setFormData({ ...formData, category: value });
                  setShowCustomCategory(value === "other");
                }}
                required
              >
                <SelectTrigger id="edit-asset-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="equipment">Equipment</SelectItem>
                  <SelectItem value="audio">Audio</SelectItem>
                  <SelectItem value="furniture">Furniture</SelectItem>
                  <SelectItem value="catering">Catering</SelectItem>
                  <SelectItem value="other">Other (Custom)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-asset-value">Value (£)</Label>
              <Input
                id="edit-asset-value"
                type="number"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
              />
            </div>
          </div>
          {showCustomCategory && (
            <div className="space-y-2">
              <Label htmlFor="edit-custom-category">Custom Category</Label>
              <Input
                id="edit-custom-category"
                placeholder="Enter custom category"
                value={formData.custom_category}
                onChange={(e) => setFormData({ ...formData, custom_category: e.target.value })}
                required
              />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="edit-asset-location">Location</Label>
            <Select
              value={formData.location}
              onValueChange={(value) => {
                setFormData({ ...formData, location: value });
                setShowCustomLocation(value === "other");
              }}
              required
            >
              <SelectTrigger id="edit-asset-location">
                <SelectValue placeholder="Select location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="garage">Garage</SelectItem>
                <SelectItem value="store_room">Store Room</SelectItem>
                <SelectItem value="shipping_container">Shipping Container</SelectItem>
                {customLocations.map(loc => (
                  <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                ))}
                <SelectItem value="other">Other (Custom)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {showCustomLocation && (
            <div className="space-y-2">
              <Label htmlFor="edit-custom-location">Custom Location</Label>
              <Input
                id="edit-custom-location"
                placeholder="Enter custom location"
                value={formData.custom_location}
                onChange={(e) => setFormData({ ...formData, custom_location: e.target.value })}
                required
              />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="edit-asset-description">Description</Label>
            <Textarea
              id="edit-asset-description"
              placeholder="Asset details..."
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>
          {loaningEnabled && (
            <div className="flex items-center justify-between p-3 border border-border rounded-lg">
              <div>
                <p className="font-medium">Loanable</p>
                <p className="text-sm text-muted-foreground">Allow this asset to be loaned out</p>
              </div>
              <Switch
                checked={formData.loanable}
                onCheckedChange={(checked) => setFormData({ ...formData, loanable: checked })}
              />
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit">Update Asset</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
